/**
 * 부트·로그인·비밀번호 재설정의 **결과를 접는 규칙.** (0218 에서 core-web 에서 옮겨 왔다)
 *
 * 웹과 앱이 같은 서버를 같은 방식으로 부른다. 응답·실패를 화면이 읽을 몇 갈래로 접는 규칙이
 * 둘에 따로 있으면 갈라진다 — 예: 한쪽만 `rateLimited` 를 알면 그쪽 화면만 "잠시 뒤에"를 말하고
 * 다른 쪽은 "비밀번호가 틀렸습니다"를 말한다. 이 규칙은 React 도 DOM 도 모르므로 통로 옆에 산다.
 * core-web 은 이 이름들을 그대로 다시 내보낸다(`session.tsx` · `boot.ts`).
 */
import { classifyRpcError, type RpcFailure } from './errors.js';

export type LoginOutcome =
  | { readonly kind: 'ok' }
  /**
   * 이메일이나 비밀번호가 틀렸다. **어느 쪽인지는 서버도 안 알려 준다** —
   * 구분해 주면 그 응답이 곧 계정 열거 도구가 된다(`auth/service.ts`).
   */
  | { readonly kind: 'rejected' }
  /**
   * **비밀번호가 맞는 회사가 둘 이상이다**(0056). 성공도 실패도 아니다 —
   * 세션은 아직 없고, 사람이 고른 뒤 같은 포트를 `tenantId` 와 함께 다시 부른다.
   */
  | { readonly kind: 'chooseCompany'; readonly companies: readonly LoginCompany[] }
  /**
   * 이 이메일에 붙은 회사가 너무 많다. **이유를 갈라서 말하는 유일한 로그인 실패다** —
   * 사용자가 스스로 못 고치는 상태라 "다시 해 보세요"가 답이 아니기 때문이다.
   */
  | { readonly kind: 'rejectedWithReason'; readonly messageKey: string; readonly nextActionKey?: string }
  | { readonly kind: 'rateLimited'; readonly retryAfterMs?: number }
  | {
      readonly kind: 'unavailable';
      readonly messageKey: string;
      readonly nextActionKey?: string;
      readonly detail?: string;
    };

export interface LoginCompany {
  readonly tenantId: string;
  readonly name: string;
}

export type LoginPort = (
  input: { email: string; password: string; tenantId?: string },
) => Promise<LoginOutcome>;

/** 로그아웃. 실패해도 화면은 익명으로 간다 — 쿠키가 남아 있어도 다음 요청이 401 이다. */
export type LogoutPort = () => Promise<void>;

/**
 * 재설정 링크 요청의 결과. **거부가 없다** — 서버가 계정의 존재를 안 알려 주기 때문이고
 * (계정 열거), 그래서 화면이 말할 수 있는 성공 문장도 "가입돼 있다면 보냈습니다"다.
 * 화면이 "보냈습니다"라고 하면 그 화면이 서버가 숨긴 것을 대신 말해 버린다(0036).
 */
export type ForgotPasswordOutcome =
  | { readonly kind: 'ok' }
  | { readonly kind: 'rateLimited'; readonly retryAfterMs?: number }
  | {
      readonly kind: 'unavailable';
      readonly messageKey: string;
      readonly nextActionKey?: string;
      readonly detail?: string;
    };

export type ForgotPasswordPort = (input: { email: string }) => Promise<ForgotPasswordOutcome>;

/**
 * 재설정 실행의 결과.
 *
 * `rejected` 가 **문장 키를 들고 온다**: 서버가 링크 무효(`auth.resetTokenInvalid`)와 길이
 * 부족(`auth.passwordTooShort`)을 갈라서 답하고, 그 둘은 사용자가 할 일이 다르다
 * (다시 요청 vs 더 길게). 화면에서 code 로 분기하면 그 분기가 화면마다 생긴다 —
 * 로그인 포트가 실패를 셋으로 접은 것과 같은 규칙이다.
 */
export type ResetPasswordOutcome =
  | { readonly kind: 'ok' }
  | { readonly kind: 'rejected'; readonly messageKey: string; readonly nextActionKey?: string }
  | { readonly kind: 'rateLimited'; readonly retryAfterMs?: number }
  | {
      readonly kind: 'unavailable';
      readonly messageKey: string;
      readonly nextActionKey?: string;
      readonly detail?: string;
    };

export type ResetPasswordPort = (
  input: { token: string; newPassword: string },
) => Promise<ResetPasswordOutcome>;

/** 통신 실패를 화면이 읽을 수 있는 한 줄로. 원본 메시지는 안 싣는다(스택·SQL 이 새는 자리다). */
function unavailableOf(failure: RpcFailure): {
  kind: 'unavailable'; messageKey: string; nextActionKey?: string; detail: string;
} {
  return {
    kind: 'unavailable',
    messageKey: failure.messageKey,
    nextActionKey: failure.nextActionKey,
    detail: `${failure.code ?? 'unclassified'}${failure.status ? ` · HTTP ${failure.status}` : ''}`,
  };
}

/** `loginPortFrom` 과 같은 규칙: **분류는 `classifyRpcError` 가 이미 한다.** */
export function forgotPasswordPortFrom(
  call: (input: { email: string }) => Promise<unknown>,
  classify: (err: unknown) => RpcFailure = classifyRpcError,
): ForgotPasswordPort {
  return async (input) => {
    try {
      await call(input);
      return { kind: 'ok' };
    } catch (e) {
      const failure = classify(e);
      if (failure.code === 'rateLimit.exceeded') {
        return { kind: 'rateLimited', retryAfterMs: retryAfterMsOf(failure) };
      }
      return unavailableOf(failure);
    }
  };
}

export function resetPasswordPortFrom(
  call: (input: { token: string; newPassword: string }) => Promise<unknown>,
  classify: (err: unknown) => RpcFailure = classifyRpcError,
): ResetPasswordPort {
  return async (input) => {
    try {
      await call(input);
      return { kind: 'ok' };
    } catch (e) {
      const failure = classify(e);
      if (failure.code === 'rateLimit.exceeded') {
        return { kind: 'rateLimited', retryAfterMs: retryAfterMsOf(failure) };
      }
      /*
       * 레지스트리 에러(422)는 **사용자가 고칠 수 있는 것**이다: 링크가 무효이거나
       * 비밀번호가 짧거나. 서버가 그 둘을 갈라서 답하므로 여기서는 문장 키를 그대로 나른다.
       * 그 밖(500·오프라인)은 `unavailable` 이다 — 하나로 접으면 서버가 죽은 날
       * 사용자 전원이 자기 링크를 의심한다.
       */
      if (failure.kind === 'app') {
        return {
          kind: 'rejected', messageKey: failure.messageKey, nextActionKey: failure.nextActionKey,
        };
      }
      return unavailableOf(failure);
    }
  };
}

/** `details.retryAfterMs` 를 꺼낸다. 모양이 다르면 `undefined` — 지어내지 않는다. */
function retryAfterMsOf(failure: RpcFailure): number | undefined {
  const d = failure.details;
  if (typeof d !== 'object' || d === null) return undefined;
  const v = (d as Record<string, unknown>).retryAfterMs;
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

/**
 * procedure 호출 하나를 로그인 결과로 접는다. `bootstrapPortFrom` 과 같은 규칙:
 * **분류는 `classifyRpcError` 가 이미 한다** — 여기서 status 를 다시 읽지 않는다.
 *
 * `unauthenticated` 만 `rejected` 다. 403·500·오프라인은 전부 `unavailable` 이다 —
 * 하나로 접으면 서버가 죽은 날 "비밀번호가 틀렸습니다"가 뜬다.
 */
export function loginPortFrom(
  call: (input: { email: string; password: string; tenantId?: string }) => Promise<unknown>,
  classify: (err: unknown) => RpcFailure = classifyRpcError,
): LoginPort {
  return async (input) => {
    try {
      const out = await call(input);
      /*
       * 성공 응답이 **둘**이다(0056). 모양을 여기서 한 번 좁힌다 — 화면이 `unknown` 을
       * 들여다보게 두면 그 판정이 화면마다 생긴다. 모르는 `kind` 가 오면 `ok` 로 본다:
       * 서버가 쿠키를 세웠다는 뜻이고, 부트가 곧 진실을 말한다.
       */
      if (typeof out === 'object' && out !== null
          && (out as { kind?: unknown }).kind === 'chooseCompany') {
        const raw = (out as { companies?: unknown }).companies;
        const companies = Array.isArray(raw) ? (raw as LoginCompany[]) : [];
        return { kind: 'chooseCompany', companies };
      }
      return { kind: 'ok' };
    } catch (e) {
      const failure = classify(e);
      if (failure.kind === 'unauthenticated') return { kind: 'rejected' };
      if (failure.code === 'rateLimit.exceeded') {
        return { kind: 'rateLimited', retryAfterMs: retryAfterMsOf(failure) };
      }
      /*
       * 레지스트리 에러(422)는 사용자가 **읽어야 하는** 실패다 — 오늘은
       * `auth.tooManyCompanies` 하나이고, 그 문장의 다음 행동이 "다시 해 보세요"가
       * 아니라 "관리자에게 문의하세요"라 `rejected` 로 접으면 안 된다.
       * `resetPasswordPortFrom` 이 같은 수법을 먼저 썼다.
       */
      if (failure.kind === 'app') {
        return {
          kind: 'rejectedWithReason',
          messageKey: failure.messageKey,
          ...(failure.nextActionKey === undefined ? {} : { nextActionKey: failure.nextActionKey }),
        };
      }
      return {
        kind: 'unavailable',
        messageKey: failure.messageKey,
        nextActionKey: failure.nextActionKey,
        // 원본 메시지를 싣지 않는다 — 스택·SQL 이 새는 자리다(`bootstrapPortFrom` 과 같다).
        detail: `${failure.code ?? 'unclassified'}${failure.status ? ` · HTTP ${failure.status}` : ''}`,
      };
    }
  };
}
